<script setup>
  // core imports
  import { ref, computed, watch, onBeforeMount, provide } from "vue";
  import { useGlobalStore } from "@/stores/global.store";
  import { useAuthStore } from "@/stores/auth.store";
  import { useCheckoutStore } from "@/stores/checkout.store";
  import { storeToRefs } from "pinia";
  import config from '@/config'
  import { parseOpts, getQueryParam } from "@/utils";
  import CheckoutEmpty from "@/components/checkout/CheckoutEmpty.vue";
  import CheckoutStep0 from "@/components/checkout/CheckoutStep0.vue";
  import CheckoutStep1 from "@/components/checkout/CheckoutStep1.vue";
  import CheckoutStep2 from "@/components/checkout/CheckoutStep2.vue";
  import CheckoutStep3 from "@/components/checkout/CheckoutStep3.vue";
  import CheckoutStep4 from "@/components/checkout/CheckoutStep4.vue";
  import CheckoutStep5 from "@/components/checkout/CheckoutStep5.vue";
  import CheckoutStep2Register from "@/components/checkout/CheckoutStep2Register.vue";
  import LoginWrap from "@/components/auth/LoginWrap.vue";
  import myButton from '@/stories/Button.vue';
  import RegisterWrap from "./components/auth/RegisterWrap.vue"
  import { useDataLayer } from "./dataLayer";
  import { DotLottieVue } from '@lottiefiles/dotlottie-vue'
import { isEmpty } from "lodash"
  const props = defineProps({
    configId: String | Number | undefined,
    componentId: String | Number | undefined,
    opts: String | undefined,
  });

  const globalStore = useGlobalStore();
  const $checkout = useCheckoutStore();
  const $auth = useAuthStore();
  const dataLayer = useDataLayer();

  const initComp = async () => {
    let options = {}
    if (props.opts) {
      if (typeof props.opts === 'string') {
        options = parseOpts(props.opts)
      } else {
        options = props.opts
      }
    }
    if(options?.checkout0?.isVMS) {
      $checkout.checkout0 = options.checkout0;
      $checkout.setStep(1)
      await $checkout.addToCart({
        item_name: `${config.vmsNamePlaceholder}${Math.floor(Math.random() * 100) + 1}.virtual`,
        item_pack: $checkout.checkout0.productPack,
        item_type: $checkout.checkout0.productType,
        item_period: $checkout.checkout0.productPeriod,
        item_ram: $checkout.checkout0?.productRam,
        item_cpu: $checkout.checkout0?.productCpu,
        item_nvme: $checkout.checkout0?.productNvme,
      });
      $checkout.isReady = true;
      return
    }
    if (options?.checkout0) {
      $checkout.checkout0 = options.checkout0;
      $checkout.setStep(0);
      $checkout.isReady = true;
      return
    }

    await $checkout.getBasketData()

    let step = getQueryParam('step');
    if ($auth.isLoggedIn) {
      await $checkout.getBillingProfiles()
    }
    if (step) {
      step = Number(step);
      if (step >= 1 && step <= 5) {
        $checkout.setStep(step);
      }
    }
    $checkout.isReady = true;
    window.addEventListener('beforeunload', handleBeforeUnload);
  };

  const handleBeforeUnload = event => {
    if ($checkout.step >= 4) {
      return true
    }
    const activeElement = event.target?.activeElement
    if (activeElement?.tagName === 'A') {
      const obj = {
        internalDestinationName: activeElement.innerText,
        internalDestinationURL: activeElement.href,
      }
      dataLayer.leaveCheckout('internal', obj)
    } else {
      dataLayer.leaveCheckout('exit')
    }
  }

  const { ready } = storeToRefs(globalStore);

  watch(ready, (val) => {
    initComp();
  });

  watch(() => $auth.isLoggedIn, (val) => {
    if (!val) {
      $checkout.setStep(1);
    }
  })

  if (ready.value) {
    initComp();
  }

const lottieLogo = {"v":"4.8.0","meta":{"g":"LottieFiles AE 3.5.1","a":"","k":"","d":"","tc":""},"fr":60,"ip":0,"op":85,"w":60,"h":60,"nm":"Comp 1","ddd":0,"assets":[],"layers":[{"ddd":0,"ind":1,"ty":4,"nm":"Shape Layer 4","sr":1,"ks":{"o":{"a":0,"k":100,"ix":11},"r":{"a":0,"k":0,"ix":10},"p":{"a":0,"k":[30,30,0],"ix":2},"a":{"a":0,"k":[0,0,0],"ix":1},"s":{"a":0,"k":[100,100,100],"ix":6}},"ao":0,"shapes":[{"ty":"gr","it":[{"ind":0,"ty":"sh","ix":1,"ks":{"a":0,"k":{"i":[[0,0],[0,0]],"o":[[0,0],[0,0]],"v":[[11.25,9.75],[20.75,-10.25]],"c":false},"ix":2},"nm":"Path 1","mn":"ADBE Vector Shape - Group","hd":false},{"ty":"st","c":{"a":0,"k":[0.988235294118,0.176470588235,0.090196078431,1],"ix":3},"o":{"a":0,"k":100,"ix":4},"w":{"a":0,"k":10,"ix":5},"lc":2,"lj":2,"bm":0,"nm":"Stroke 1","mn":"ADBE Vector Graphic - Stroke","hd":false},{"ty":"tr","p":{"a":0,"k":[-0.875,0.125],"ix":2},"a":{"a":0,"k":[0,0],"ix":1},"s":{"a":0,"k":[100,100],"ix":3},"r":{"a":0,"k":0,"ix":6},"o":{"a":0,"k":100,"ix":7},"sk":{"a":0,"k":0,"ix":4},"sa":{"a":0,"k":0,"ix":5},"nm":"Transform"}],"nm":"Shape 1","np":3,"cix":2,"bm":0,"ix":1,"mn":"ADBE Vector Group","hd":false},{"ty":"tm","s":{"a":1,"k":[{"i":{"x":[0.833],"y":[0.833]},"o":{"x":[0.167],"y":[0.167]},"t":62,"s":[0]},{"t":65,"s":[100]}],"ix":1},"e":{"a":1,"k":[{"i":{"x":[0.833],"y":[0.833]},"o":{"x":[0.167],"y":[0.167]},"t":21,"s":[0]},{"t":30,"s":[100]}],"ix":2},"o":{"a":0,"k":0,"ix":3},"m":1,"ix":2,"nm":"Trim Paths 1","mn":"ADBE Vector Filter - Trim","hd":false}],"ip":0,"op":85,"st":0,"bm":0},{"ddd":0,"ind":2,"ty":4,"nm":"Shape Layer 3","sr":1,"ks":{"o":{"a":0,"k":100,"ix":11},"r":{"a":0,"k":0,"ix":10},"p":{"a":0,"k":[30,30,0],"ix":2},"a":{"a":0,"k":[0,0,0],"ix":1},"s":{"a":0,"k":[100,100,100],"ix":6}},"ao":0,"shapes":[{"ty":"gr","it":[{"ind":0,"ty":"sh","ix":1,"ks":{"a":0,"k":{"i":[[0,0],[0,0]],"o":[[0,0],[0,0]],"v":[[1.312,-10.188],[11.25,9.75]],"c":false},"ix":2},"nm":"Path 1","mn":"ADBE Vector Shape - Group","hd":false},{"ty":"st","c":{"a":0,"k":[0,0.41568627451,0.717647058824,1],"ix":3},"o":{"a":0,"k":100,"ix":4},"w":{"a":0,"k":10,"ix":5},"lc":2,"lj":2,"bm":0,"nm":"Stroke 1","mn":"ADBE Vector Graphic - Stroke","hd":false},{"ty":"tr","p":{"a":0,"k":[-0.875,0.125],"ix":2},"a":{"a":0,"k":[0,0],"ix":1},"s":{"a":0,"k":[100,100],"ix":3},"r":{"a":0,"k":0,"ix":6},"o":{"a":0,"k":100,"ix":7},"sk":{"a":0,"k":0,"ix":4},"sa":{"a":0,"k":0,"ix":5},"nm":"Transform"}],"nm":"Shape 1","np":3,"cix":2,"bm":0,"ix":1,"mn":"ADBE Vector Group","hd":false},{"ty":"tm","s":{"a":1,"k":[{"i":{"x":[0.833],"y":[0.833]},"o":{"x":[0.167],"y":[0.167]},"t":57,"s":[0]},{"t":62,"s":[100]}],"ix":1},"e":{"a":1,"k":[{"i":{"x":[0.833],"y":[0.833]},"o":{"x":[0.167],"y":[0.167]},"t":14,"s":[0]},{"t":21,"s":[100]}],"ix":2},"o":{"a":0,"k":0,"ix":3},"m":1,"ix":2,"nm":"Trim Paths 1","mn":"ADBE Vector Filter - Trim","hd":false}],"ip":0,"op":85,"st":0,"bm":0},{"ddd":0,"ind":3,"ty":4,"nm":"Shape Layer 2","sr":1,"ks":{"o":{"a":0,"k":100,"ix":11},"r":{"a":0,"k":0,"ix":10},"p":{"a":0,"k":[30,30,0],"ix":2},"a":{"a":0,"k":[0,0,0],"ix":1},"s":{"a":0,"k":[100,100,100],"ix":6}},"ao":0,"shapes":[{"ty":"gr","it":[{"ind":0,"ty":"sh","ix":1,"ks":{"a":0,"k":{"i":[[0,0],[0,0]],"o":[[0,0],[0,0]],"v":[[-8,10.375],[1.312,-10.188]],"c":false},"ix":2},"nm":"Path 1","mn":"ADBE Vector Shape - Group","hd":false},{"ty":"st","c":{"a":0,"k":[0,0.819607843137,1,1],"ix":3},"o":{"a":0,"k":100,"ix":4},"w":{"a":0,"k":10,"ix":5},"lc":2,"lj":2,"bm":0,"nm":"Stroke 1","mn":"ADBE Vector Graphic - Stroke","hd":false},{"ty":"tr","p":{"a":0,"k":[-0.875,0.125],"ix":2},"a":{"a":0,"k":[0,0],"ix":1},"s":{"a":0,"k":[100,100],"ix":3},"r":{"a":0,"k":0,"ix":6},"o":{"a":0,"k":100,"ix":7},"sk":{"a":0,"k":0,"ix":4},"sa":{"a":0,"k":0,"ix":5},"nm":"Transform"}],"nm":"Shape 1","np":3,"cix":2,"bm":0,"ix":1,"mn":"ADBE Vector Group","hd":false},{"ty":"tm","s":{"a":1,"k":[{"i":{"x":[0.833],"y":[0.833]},"o":{"x":[0.167],"y":[0.167]},"t":50,"s":[0]},{"t":57,"s":[100]}],"ix":1},"e":{"a":1,"k":[{"i":{"x":[0.833],"y":[0.833]},"o":{"x":[0.167],"y":[0.167]},"t":7,"s":[0]},{"t":14,"s":[100]}],"ix":2},"o":{"a":0,"k":0,"ix":3},"m":1,"ix":2,"nm":"Trim Paths 1","mn":"ADBE Vector Filter - Trim","hd":false}],"ip":0,"op":85,"st":0,"bm":0},{"ddd":0,"ind":4,"ty":4,"nm":"Shape Layer 1","sr":1,"ks":{"o":{"a":0,"k":100,"ix":11},"r":{"a":0,"k":0,"ix":10},"p":{"a":0,"k":[30,30,0],"ix":2},"a":{"a":0,"k":[0,0,0],"ix":1},"s":{"a":0,"k":[100,100,100],"ix":6}},"ao":0,"shapes":[{"ty":"gr","it":[{"ind":0,"ty":"sh","ix":1,"ks":{"a":0,"k":{"i":[[0,0],[0,0],[0,0],[0,0]],"o":[[0,0],[0,0],[0,0],[0,0]],"v":[[-18.375,-10.5],[-18.375,-10.5],[-18.375,-10.5],[-8,10.375]],"c":false},"ix":2},"nm":"Path 1","mn":"ADBE Vector Shape - Group","hd":false},{"ty":"st","c":{"a":0,"k":[0.29411765933,0.917647063732,0.984313726425,1],"ix":3},"o":{"a":0,"k":100,"ix":4},"w":{"a":0,"k":10,"ix":5},"lc":2,"lj":2,"bm":0,"nm":"Stroke 1","mn":"ADBE Vector Graphic - Stroke","hd":false},{"ty":"tr","p":{"a":0,"k":[-0.875,0.125],"ix":2},"a":{"a":0,"k":[0,0],"ix":1},"s":{"a":0,"k":[100,100],"ix":3},"r":{"a":0,"k":0,"ix":6},"o":{"a":0,"k":100,"ix":7},"sk":{"a":0,"k":0,"ix":4},"sa":{"a":0,"k":0,"ix":5},"nm":"Transform"}],"nm":"Shape 1","np":3,"cix":2,"bm":0,"ix":1,"mn":"ADBE Vector Group","hd":false},{"ty":"tm","s":{"a":1,"k":[{"i":{"x":[0.833],"y":[0.833]},"o":{"x":[0.167],"y":[0.167]},"t":40,"s":[0]},{"t":50,"s":[100]}],"ix":1},"e":{"a":1,"k":[{"i":{"x":[0.833],"y":[0.833]},"o":{"x":[0.167],"y":[0.167]},"t":0,"s":[0]},{"t":5,"s":[100]}],"ix":2},"o":{"a":0,"k":0,"ix":3},"m":1,"ix":2,"nm":"Trim Paths 1","mn":"ADBE Vector Filter - Trim","hd":false}],"ip":0,"op":85,"st":0,"bm":0}],"markers":[]}

const isFailedPayment = computed(() => {
  return getQueryParam('payment') === 'failed' && isEmpty($checkout.checkout5)
    // $checkout.checkout5 = $checkout.getFailedPaymentData
})

provide('isFailedPayment', isFailedPayment.value)
</script>

<template>
  <div v-if="$checkout.isReady">
    <CheckoutStep0 v-if="$checkout.step === 0" />

    <div v-else>
      <CheckoutEmpty v-if="$checkout.isEmptyCheckout && !isFailedPayment" />
      <CheckoutStep1 v-else-if="$checkout.step === 1" />
      <div v-else-if="$checkout.step === 2">
        <CheckoutStep2 v-if="$auth.isLoggedIn" />
        <CheckoutStep2Register v-else />
      </div>
      <CheckoutStep3 v-else-if="$checkout.step === 3" />
      <CheckoutStep4 v-else-if="$checkout.step === 4" />
      <CheckoutStep5 v-else-if="$checkout.step === 5" />
    </div>
    <Teleport to="body">
      <div class="modal modal--ignore-custom-top-offset modal-login--checkout fade" id="checkoutLoginModal" tabindex="-1" aria-labelledby="Checkout Login" aria-hidden="true">
        <div class="modal-dialog modal-dialog-scrollable modal-sm">
          <div class="modal-content">
            <div class="modal-body modal-login--checkout__body">
              <myButton variant="link" color="gray" size="medium" class="modal-close" data-bs-dismiss="modal" iconLeft='<svg xmlns="http://www.w3.org/2000/svg" fill="none"><path fill="#7D7F8A" fill-rule="evenodd" d="M21.828 6.175a1.167 1.167 0 0 1 0 1.65l-14 14a1.167 1.167 0 0 1-1.65-1.65l14-14a1.167 1.167 0 0 1 1.65 0Z" clip-rule="evenodd"/><path fill="#7D7F8A" fill-rule="evenodd" d="M6.178 6.175a1.167 1.167 0 0 1 1.65 0l14 14a1.167 1.167 0 1 1-1.65 1.65l-14-14a1.167 1.167 0 0 1 0-1.65Z" clip-rule="evenodd"/></svg>' />
              <LoginWrap variant="checkout" />
            </div>
          </div>
        </div>
      </div>
      <div class="modal modal-register--checkout fade" id="checkoutRegisterModal" tabindex="-1" aria-labelledby="Checkout Login" aria-hidden="true">
        <div class="modal-dialog modal-dialog-scrollable modal-sm">
          <div class="modal-content">
            <div class="modal-body modal-register--checkout__body">
              <myButton variant="link" color="gray" size="medium" class="modal-close" data-bs-dismiss="modal" iconLeft='<svg xmlns="http://www.w3.org/2000/svg" fill="none"><path fill="#7D7F8A" fill-rule="evenodd" d="M21.828 6.175a1.167 1.167 0 0 1 0 1.65l-14 14a1.167 1.167 0 0 1-1.65-1.65l14-14a1.167 1.167 0 0 1 1.65 0Z" clip-rule="evenodd"/><path fill="#7D7F8A" fill-rule="evenodd" d="M6.178 6.175a1.167 1.167 0 0 1 1.65 0l14 14a1.167 1.167 0 1 1-1.65 1.65l-14-14a1.167 1.167 0 0 1 0-1.65Z" clip-rule="evenodd"/></svg>' />
              <div class="b-checkout-register__hero">
                <h4 class="b-checkout-register__hero__title">{{ $t('auth.register.title') }}</h4>
                <p class="b-checkout-register__hero__subtitle">{{ $t('auth.register.subtitle') }}</p>
              </div>
              <RegisterWrap variant="checkout" />
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
  <div v-else>
    <DotLottieVue :data="lottieLogo" class="dottie" autoplay loop />
  </div>

</template>

<style scoped>
.dottie {
  width: 300px;
  margin: 30px auto;
}
</style>
